import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import configService from '../services/config';

// MUI Imports
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  Stack,
  Button
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

export default function AttendanceRegularizationList() {
  const { user_id, company_id, is_super, companyName } = useSelector(
    (state) => state.GlobalVariables
  );

  const [regularizations, setRegularizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [regRes] = await Promise.all([
        configService.getRegularizationList({ company_id: company_id, employee_id: user_id }),
      ]);

      if (regRes?.status) {
        setRegularizations(regRes.data || []);
      } else {
        toast.error(regRes?.message || 'No regularization data found');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load data. Check network or backend.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [company_id, user_id]);

  const handleApprove = async (id) => {
    setActionLoadingId(id);
    try {
      const res = await configService.approveAttendanceRegularization(id, {
        admin_remarks: 'hello',
      });
      if (res?.status) {
        toast.success(res.message || 'Approved successfully');
        const newStatus = res.data?.status || 'Approved';
        setRegularizations((prev) =>
          prev.map((r) => (r.id === id ? { ...r, status: newStatus, admin_remarks: res.data?.admin_remarks } : r))
        );
      } else {
        toast.error(res?.message || 'Failed to approve');
      }
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Something went wrong while approving');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeny = async (id) => {
    const reason = window.prompt('Please enter a reason for rejecting this request:');

    if (reason === null) return;
    if (!reason.trim()) {
      toast.error('A reason is required to reject this request');
      return;
    }

    setActionLoadingId(id);
    try {
      const res = await configService.denyAttendanceRegularization(id, {
        admin_remarks: reason.trim(),
      });

      if (res?.status) {
        toast.success(res.message || 'Rejected successfully');
        const newStatus = res.data?.status || 'Denied';
        setRegularizations((prev) =>
          prev.map((r) => (r.id === id ? { ...r, status: newStatus, admin_remarks: res.data?.admin_remarks } : r))
        );
      } else {
        toast.error(res?.message || 'Failed to reject');
      }
    } catch (error) {
      console.error('Deny error:', error);
      toast.error('Something went wrong while rejecting');
    } finally {
      setActionLoadingId(null);
    }
  };

  const getCustomerName = (id) => {
    const c = customers.find((c) => String(c.id) === String(id));
    return c?.fname || id;
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  // Helper for MUI Chip colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
      case 'Denied':
        return 'error';
      case 'Pending':
      default:
        return 'warning';
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const paginatedRows = regularizations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: '100%', height: '100%', minHeight: '100vh', boxSizing: 'border-box' }}>

      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" fontWeight="bold" color="text.primary"sx={{color: '#1976d2'}}>
          Attendance Regularization
        </Typography>
      </Box>

      {/* Table Section */}
      {regularizations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }} elevation={0} variant="outlined">
          <Typography color="text.secondary">No regularization requests found.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
          <Table sx={{ width: '100%' }} aria-label="regularization table">
            <TableHead>
              <TableRow sx={{ bgcolor: '#1976d2' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Customer Id</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Time Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Entry Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Remarks</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff', minWidth: 200 }}>Status & Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((r) => {
                const status = r.status || 'Pending';
                const isBusy = actionLoadingId === r.id;

                return (
                  <TableRow
                    key={r.id}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': { bgcolor: 'grey.50' },
                    }}
                  >
                    <TableCell>{getCustomerName(r.customer_id)}</TableCell>
                    <TableCell>{r.category}</TableCell>
                    <TableCell>{r.time_type}</TableCell>
                    <TableCell>{fmtDate(r.entry_date)}</TableCell>
                    <TableCell>{r.remarks || '—'}</TableCell>
                    <TableCell>
                      <Stack spacing={1}>
                        <Box>
                          <Chip
                            label={status}
                            color={getStatusColor(status)}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>

                        {status === 'Pending' && (
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={isBusy ? <CircularProgress size={12} color="inherit" /> : <CheckIcon />}
                              onClick={() => handleApprove(r.id)}
                              disabled={isBusy}
                              sx={{ textTransform: 'none', px: 1, py: 0.2, minWidth: 'auto', fontSize: '0.75rem' }}
                              disableElevation
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              startIcon={isBusy ? <CircularProgress size={12} color="inherit" /> : <CloseIcon />}
                              onClick={() => handleDeny(r.id)}
                              disabled={isBusy}
                              sx={{ textTransform: 'none', px: 1, py: 0.2, minWidth: 'auto', fontSize: '0.75rem' }}
                              disableElevation
                            >
                              Reject
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={regularizations.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      )}
    </Box>
  );
}